import { t } from '../../i18n';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import type { TextInputPopupHandle } from '../TextInputPopup';
import { createActionButton } from './domFactories';
import type { FriendDisplay, GuildMemberDisplay, SocialTab } from './types';

interface RenderSocialTabParams {
    list: HTMLDivElement;
    footer: HTMLDivElement;
    activeSocialTab: SocialTab;
    friendRows: FriendDisplay[];
    guildName: string | null;
    guildMembers: GuildMemberDisplay[];
    inputPopup: TextInputPopupHandle;
    spaceTimeClient: SpaceTimeClient;
    handleRemoveFriend: (name: string) => void;
    handleLeaveGuild: () => void;
}

export function renderSocialTab(params: RenderSocialTabParams): void {
    const {
        list,
        footer,
        activeSocialTab,
        friendRows,
        guildName,
        guildMembers,
        inputPopup,
        spaceTimeClient,
        handleRemoveFriend,
        handleLeaveGuild,
    } = params;

    if (activeSocialTab === 'Friends') {
        const addBtn = createActionButton(t('userMenu.socials.addFriend'), '#3b82f6');
        addBtn.addEventListener('click', () => {
            inputPopup.show(t('userMenu.socials.addFriendTitle'), async (text) => {
                try {
                    await spaceTimeClient.social.addFriend(text);
                } catch (e) {
                    console.error('Add friend failed', e);
                    alert(t('userMenu.socials.addFriendFailed', { error: String(e) }));
                }
            });
        });

        if (friendRows.length === 0) {
            const empty = document.createElement('div');
            empty.textContent = t('userMenu.socials.friendsEmpty');
            Object.assign(empty.style, { textAlign: 'center', color: '#6b7280', marginTop: '32px' });
            list.appendChild(empty);
        } else {
            friendRows.forEach((f) => {
                const item = document.createElement('div');
                Object.assign(item.style, {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '4px',
                });

                const nameSpan = document.createElement('span');
                nameSpan.textContent = f.name;
                nameSpan.style.color = f.status === 'Online' ? '#ffffff' : '#6b7280';

                const removeBtn = document.createElement('button');
                removeBtn.textContent = t('userMenu.socials.removeFriend');
                removeBtn.type = 'button';
                Object.assign(removeBtn.style, {
                    padding: '4px 8px',
                    backgroundColor: '#374151',
                    color: '#9ca3af',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                });
                removeBtn.addEventListener('click', () => handleRemoveFriend(f.name));

                item.appendChild(nameSpan);
                item.appendChild(removeBtn);
                list.appendChild(item);
            });
        }

        footer.appendChild(addBtn);
        return;
    }

    if (guildName) {
        const guildHeader = document.createElement('div');
        guildHeader.textContent = guildName;
        Object.assign(guildHeader.style, {
            fontSize: '1.2rem',
            fontWeight: 'bold',
            marginBottom: '12px',
            color: '#60a5fa',
            textAlign: 'center',
        });
        list.appendChild(guildHeader);

        const table = document.createElement('div');
        Object.assign(table.style, {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            backgroundColor: '#141820',
            borderRadius: '6px',
            padding: '12px',
        });

        for (let i = 0; i < guildMembers.length; i += 2) {
            const left = guildMembers[i];
            const right = guildMembers[i + 1];
            table.appendChild(renderGuildMemberCell(left));
            table.appendChild(renderGuildMemberCell(right));
        }

        list.appendChild(table);

        const leaveBtn = createActionButton(t('userMenu.guild.leave'), '#ef4444');
        leaveBtn.addEventListener('click', handleLeaveGuild);
        footer.appendChild(leaveBtn);
        return;
    }

    const empty = document.createElement('div');
    empty.textContent = t('userMenu.guild.notInGuild');
    Object.assign(empty.style, { textAlign: 'center', color: '#6b7280', marginTop: '32px' });
    list.appendChild(empty);

    const joinBtn = createActionButton(t('userMenu.guild.join'), '#3b82f6');
    joinBtn.addEventListener('click', () => {
        inputPopup.show(t('userMenu.guild.joinTitle'), async (text) => {
            try {
                await spaceTimeClient.social.joinGuild(text);
            } catch (e) {
                console.error('Join guild failed', e);
                alert(t('userMenu.guild.joinFailed', { error: String(e) }));
            }
        });
    });

    const createBtn = createActionButton(t('userMenu.guild.create'), '#3b82f6');
    createBtn.addEventListener('click', () => {
        inputPopup.show(t('userMenu.guild.createTitle'), async (text) => {
            try {
                await spaceTimeClient.social.createGuild(text);
            } catch (e) {
                console.error('Create guild failed', e);
                alert(t('userMenu.guild.createFailed', { error: String(e) }));
            }
        });
    });

    footer.appendChild(joinBtn);
    footer.appendChild(createBtn);
}

function renderGuildMemberCell(member?: GuildMemberDisplay): HTMLDivElement {
    const cell = document.createElement('div');
    Object.assign(cell.style, {
        minHeight: '40px',
        padding: '8px',
    });
    if (!member) return cell;

    const name = document.createElement('div');
    name.textContent = t('userMenu.guild.memberFormat', { name: member.name, rank: member.rank });
    name.style.color = member.status === 'Online' ? '#ffffff' : '#6b7280';
    cell.appendChild(name);
    return cell;
}
